import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import React, { useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { TrackEvent } from '../../../analytics';
import { CustomTextField } from '../../components';
import useCreatePreviewSurveys from '../../hooks/useCreatePreviewSurveys';
import useGetPreviewSurveys from '../../hooks/useGetPreviewSurveys';
import usePollPreviewSurveysStatus from '../../hooks/usePollPreviewSurveysStatus';

// Possible job states
const JobStates = {
  Active: 'active',
  Failed: 'failed',
  Completed: 'completed',
};

// Slide transition
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ----- STYLED COMPONENTS -----
const ContentContainer = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const NoteContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
  backgroundColor: '#FFFFFF',
  border: '1px solid #C7D6DF',
  borderRadius: '15px',
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const AssetLinkRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const GenerateButton = styled(Button)(({ theme }) => ({
  width: 136,
  height: 40,
  fontSize: 13,
  fontWeight: 600,
  borderRadius: '20px',
  color: '#FFFFFF',
  backgroundColor: '#3B6CAB',
  textTransform: 'none',
  ':hover': {
    backgroundColor: '#335B8F',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.25)',
    color: '#FFFFFF',
    border: 'none',
  },
}));

export default function SurveyPreviewModal({ open, onClose, wevoId, hasGenerateAction, numPages }) {
  const { track } = useAnalytics();

  // Local State
  const [previewSurveys, setPreviewSurveys] = useState([]);
  const [currentJobIds, setCurrentJobIds] = useState([]);
  const [copiedLinkIndex, setCopiedLinkIndex] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPollingActive, setIsPollingActive] = useState(false);

  // ----- HOOKS (React Query) -----
  const { isLoading: isLoadingPreview, refetch: refetchPreviewData } = useGetPreviewSurveys(
    { wevoId },
    {
      enabled: false, // prevent auto-fetch on mount
      onSuccess: (data) => {
        if (data?.previewSurveys) {
          const valid = data.previewSurveys.filter((obj) => {
            return obj && Object.keys(obj).length > 0 && (obj.id || obj.testUrl);
          });

          setPreviewSurveys(valid);
        }
      },
    }
  );

  usePollPreviewSurveysStatus(
    { wevoId, jobIds: currentJobIds },
    {
      onSuccess: async (data) => {
        const statuses = data?.statuses || [];

        if (!statuses.length) {
          setIsPollingActive(false);
        }

        const anyFailed = statuses.some((s) => s.status === JobStates.Failed);
        const allCompleted = statuses.every((s) => s.status === JobStates.Completed);

        if (anyFailed) {
          setIsPollingActive(false);
          setCurrentJobIds([]);
          setIsGenerating(false);
          setErrorMsg('There was a problem generating your link(s). Please try again.');
        }

        if (allCompleted) {
          setCurrentJobIds([]);
          setIsPollingActive(false);
          await refetchPreviewData();
          setIsGenerating(false);
        }
      },
      onError: (err) => {
        setIsPollingActive(false);
        setCurrentJobIds([]);
        setIsGenerating(false);
        setErrorMsg('There was a problem generating your link(s). Please try again.');
      },
      enabled: isPollingActive && currentJobIds.length > 0,
      refetchInterval: isPollingActive && currentJobIds.length > 0 ? 2000 : null,
      refetchIntervalInBackground: true,
    }
  );

  const { mutateAsync: createPreviewSurveysAsync } = useCreatePreviewSurveys();

  // ----------------- EVENT HANDLERS -----------------

  const handleGenerateClick = async () => {
    setIsGenerating(true);
    setPreviewSurveys([]);
    setErrorMsg(null);
    setCurrentJobIds([]);
    track(TrackEvent.CLICKED_GENERATE_SURVEY_PREVIEW_LINK, { wevoId });
    try {
      const jobs = await createPreviewSurveysAsync({ wevoId });
      const jobIds = Object.keys(jobs);

      if (jobIds?.length) {
        setCurrentJobIds(jobIds);
        setIsPollingActive(true);
      }
    } catch (err) {
      setIsGenerating(false);
      setErrorMsg('There was a problem generating your link(s). Please try again.');
    }
  };

  const handleCopyLink = (link, index) => {
    navigator.clipboard.writeText(link);

    // Store which link was copied
    setCopiedLinkIndex(index);

    // Reset after 1 second
    setTimeout(() => {
      setCopiedLinkIndex(null);
    }, 1000);

    track(TrackEvent.COPIED_SURVEY_PREVIEW_LINK, { wevoId });
  };

  const handleClose = () => {
    setErrorMsg(null);
    onClose();
  };

  // ----------------- RENDER -----------------

  const renderAssetLinks = () => {
    if (isGenerating || !!errorMsg || !previewSurveys.length) return null;

    return previewSurveys.map((survey, idx) => {
      const label = previewSurveys.length > 1 ? `Asset ${idx + 1}` : 'Asset';
      const isLast = idx === previewSurveys.length - 1;
      const showCopied = copiedLinkIndex === idx;

      return (
        <Box key={`${survey.id}-${idx}`} sx={{ mb: isLast ? 0 : 1.5 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              mb: 0.5,
              color: '#272B30',
              display: 'flex',
              alignItems: 'center',
            }}>
            {label}
            {survey.isStale && (
              <Tooltip
                title="This preview may be outdated. Please regenerate to see the latest changes."
                arrow>
                <ErrorOutlineIcon
                  sx={{
                    marginLeft: 0.5,
                    color: '#F44336',
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <AssetLinkRow>
            <CustomTextField value={survey.testUrl || ''} InputProps={{ readOnly: true }} />
            <Box sx={{ width: '100px', height: '40px' }}>
              {showCopied ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: 'inherit', height: 'inherit' }}>
                  <Typography sx={{ color: (theme) => theme.palette.secondary.main, fontSize: 12 }}>
                    Copied!
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => handleCopyLink(survey.testUrl, idx)}
                  sx={{
                    width: 'inherit',
                    height: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: '20px',
                    backgroundColor: '#3B6CAB',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    ':hover': {
                      backgroundColor: '#335B8F',
                    },
                  }}>
                  Copy link
                </Button>
              )}
            </Box>
          </AssetLinkRow>
        </Box>
      );
    });
  };

  const renderBodyContent = () => {
    // If loading surveys (and not polling)
    if (isLoadingPreview && !isPollingActive) {
      return (
        <Box display="flex" justifyContent="center" mt={3} sx={{ minWidth: { md: 400, lg: 500 } }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <>
        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: '20px', color: '#4F5459', mb: 2 }}>
          Previews are a close approximation of what the respondents will see. However, you will be able to
          navigate the survey questions without being screened out.
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: '20px', color: '#4F5459', mb: 2 }}>
          If you encounter issues reaching your survey, please try accessing the link in an incognito browsing
          window.
        </Typography>
        <NoteContainer>
          <Box sx={{ display: 'flex', alignItems: 'start' }}>
            <ErrorOutlineIcon />
          </Box>
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            <strong>Note:</strong> Preview links will include all the input at the time of generation. Any
            additional changes will require the generation of a new link.
          </Typography>
        </NoteContainer>
        {(errorMsg || (!hasGenerateAction && !errorMsg)) && (
          <Box
            mb={2}
            p={1}
            sx={{
              backgroundColor: '#FDF3F3',
              border: '1px solid #FBCBCB',
              borderRadius: 2,
            }}>
            {errorMsg && (
              <Typography variant="body2" color="error" align="center" sx={{ fontSize: 13 }}>
                {errorMsg}
              </Typography>
            )}
            {!hasGenerateAction && !errorMsg && (
              <Typography variant="body2" color="error" align="center" sx={{ fontSize: 13 }}>
                The Wevo is missing information that is necessary to generate a link. Please visit the review
                page to see the missing fields, then try again.
              </Typography>
            )}
          </Box>
        )}
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <GenerateButton disabled={!hasGenerateAction || isGenerating} onClick={handleGenerateClick}>
            {isGenerating ? 'Generating...' : 'Generate link'}
          </GenerateButton>
          <Typography variant="body2" sx={{ mt: 1, fontSize: 11 }}>
            Estimated time: <strong>{numPages} min</strong>
          </Typography>
          {isGenerating && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={22} />
            </Box>
          )}
        </Box>
        {renderAssetLinks()}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-labelledby="generate-survey-preview"
      TransitionProps={{ onEnter: refetchPreviewData }}
      PaperProps={{
        sx: { borderRadius: '10px' },
      }}>
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ color: '#272B30' }}>
            Generate survey preview link
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.text.primary,
            })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <ContentContainer>{renderBodyContent()}</ContentContainer>
    </Dialog>
  );
}
